<script lang="ts">
  import type { CardProps } from '$lib/types/contentTypes';
  import { browser } from '$app/environment';
  import { CARD_PANEL_HEADING_ALIGNMENT, CARD_PANEL_VARIANT } from '$lib/constants/constants';
  import Carousel from 'svelte-carousel';
  import Card from '$lib/components/website/Card.svelte';
  import ArrowButton from '../control/ArrowButton.svelte';
  import Viewport from '../utility/Viewport.svelte';

  export let heading: string = '';
  export let subHeading: string = '';
  export let cardsCollection: [CardProps];
  export let variant: string;
  export let headingAlignment: string = '';
  export let customClass: string = '';

  export let customGridPanelClass: string =
    'flex flex-col gap-8 justify-center items-start px-4 mt-8 md:mt-12 lg:px-0 lg:flex-row lg:gap-x-8 lg:gap-y-16';

  const itemCount: number = cardsCollection?.length || 0;
  const DESKTOP_ITEM_WIDTH = 384; //Px
  const MOBILE_ITEM_WIDTH = 320; //Px
  const DESKTOP_ITEM_GAP = 32; //Px
  const MOBILE_ITEM_GAP = 24; //Px
  const DESKTOP_PADDING = 32; //Px
  const MOBILE_PADDING = 16; //Px

  let carousel: any;
  let width: number = 0;
  let max_items: number = 1;
  let pageIndex = 0;

  // 1216 is the max width of the container
  $: carouselWidth = width >= 1216 ? 1216 : width;

  // Calculates the movement of the last item in the carousel
  const isAtEnd = (pageIndex) => pageIndex >= itemCount - Math.floor(max_items);
  $: position = isAtEnd(pageIndex) ? 'last' : null;

  // 1024 is the lg breakpoint and the
  $: if (carouselWidth >= 1024) {
    max_items = carouselWidth / (DESKTOP_ITEM_WIDTH + DESKTOP_ITEM_GAP - DESKTOP_PADDING);
  } else {
    max_items = carouselWidth / (MOBILE_ITEM_WIDTH + MOBILE_ITEM_GAP - MOBILE_PADDING);
  }

  const handleNextClick = () => {
    carousel.goToNext();
  };
  const handlePrevClick = () => {
    carousel.goToPrev();
  };

  $: textContainer =
    headingAlignment === CARD_PANEL_HEADING_ALIGNMENT.CENTER ? 'text-center' : 'text-left';

  $: buttonContainer =
    headingAlignment === CARD_PANEL_HEADING_ALIGNMENT.CENTER ? 'justify-center' : 'justify-left';
</script>

<section class={customClass}>
  <Viewport bind:width />
  {#if heading}
    <div class="flex flex-col gap-3 {textContainer}">
      <p class="text-sm text-primary-500 font-semibold lg:text-md">{subHeading}</p>
      <h2 class="text-30 text-gray-900 font-semibold lg:text-display-md">{heading}</h2>
    </div>
  {/if}
  {#if variant === CARD_PANEL_VARIANT.SLIDER && itemCount > max_items}
    {#if browser && itemCount}
      <div
        class="justify-center items-start pl-0 carousel-container mt-8 lg:mt-12 w-[calc(100%+1rem)]"
        {position}
      >
        {#key max_items}
          <Carousel
            bind:this={carousel}
            arrows={false}
            dots={false}
            infinite={false}
            particlesToShow={max_items}
            particlesToScroll={1}
            on:pageChange={(event) => (pageIndex = event.detail)}
          >
            {#each cardsCollection as card}
              <div class="carousel-item">
                <Card {...card} horizontalSmallDevice={false} />
              </div>
            {/each}
          </Carousel>
        {/key}
      </div>
      {#if itemCount > max_items}
        <div
          class="items-center gap-4 px-4 flex my-7 lg:px-0 sm:mt-12 flex-row w-full justify-center {buttonContainer}"
        >
          {#if pageIndex == 0}
            <ArrowButton direction="left" arrowStyle="pointer-events-none opacity-50" />
          {:else}
            <ArrowButton direction="left" on:click={handlePrevClick} />
          {/if}
          {#if position == 'last'}
            <ArrowButton direction="right" arrowStyle="pointer-events-none opacity-50" />
          {:else}
            <ArrowButton direction="right" on:click={handleNextClick} />
          {/if}
        </div>
      {/if}
    {/if}
  {:else}
    <div class={customGridPanelClass}>
      {#each cardsCollection as card}
        <Card {...card} horizontalSmallDevice={variant !== CARD_PANEL_VARIANT.SLIDER} />
      {/each}
    </div>
  {/if}
</section>

<style>
  .carousel-item {
    transition: margin 400ms ease-in-out;
  }
  :global(.carousel-item > a) {
    padding-right: 1.5rem;
  }
</style>
